import React, { useRef, useState } from "react";
import { ArrowLeft, Camera, Video as VideoIcon, Mic, FolderOpen, X, Play, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { uploadMedia, mediaUrl } from "../../lib/api";
import { AudioRecorder } from "./AudioRecorder";

const MAX_PHOTOS = 3;
const MAX_VIDEOS = 1;
const MAX_AUDIO = 1;
const VIDEO_MAX_SECONDS = 30;
const AUDIO_MAX_SECONDS = 60;
const PHOTO_MAX_DIM = 1600;       // longest edge after resize
const PHOTO_JPEG_QUALITY = 0.82;

const countByKind = (atts, kind) => atts.filter((a) => a.kind === kind).length;

// Browser-native image downscaling. Keeps photos sharp but cuts upload
// size by 60–85% on typical phone cameras. Falls back to the original
// file if anything goes wrong.
const resizePhoto = (file) => new Promise((resolve) => {
  const img = new Image();
  const reader = new FileReader();
  reader.onerror = () => resolve(file);
  reader.onload = () => {
    img.onerror = () => resolve(file);
    img.onload = () => {
      const longest = Math.max(img.width, img.height);
      if (longest <= PHOTO_MAX_DIM) { resolve(file); return; }
      const ratio = PHOTO_MAX_DIM / longest;
      const w = Math.round(img.width * ratio);
      const h = Math.round(img.height * ratio);
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) { resolve(file); return; }
      ctx.drawImage(img, 0, 0, w, h);
      canvas.toBlob((blob) => {
        if (!blob) { resolve(file); return; }
        resolve(new File([blob], file.name.replace(/\.(heic|heif|png)$/i, ".jpg"),
                         { type: "image/jpeg", lastModified: Date.now() }));
      }, "image/jpeg", PHOTO_JPEG_QUALITY);
    };
    img.src = reader.result;
  };
  reader.readAsDataURL(file);
});

const readMediaDuration = (file, kind) => new Promise((resolve) => {
  const el = document.createElement(kind === "video" ? "video" : "audio");
  el.preload = "metadata";
  el.onloadedmetadata = () => {
    URL.revokeObjectURL(el.src);
    resolve(el.duration || 0);
  };
  el.onerror = () => { URL.revokeObjectURL(el.src); resolve(0); };
  el.src = URL.createObjectURL(file);
});

const ActionCard = ({ icon: Icon, label, hint, onPrimary, onSecondary, testid }) => (
  <div
    className="bg-white dark:bg-zinc-900 rounded-2xl shadow-sm border border-border/60 p-4 flex items-center gap-3 active:scale-[0.99] transition-transform"
    data-testid={testid}
  >
    <button
      type="button"
      onClick={onPrimary}
      className="flex-1 flex items-center gap-3 text-left"
      data-testid={`${testid}-primary`}
    >
      <span className="w-11 h-11 rounded-xl bg-mali-red/10 text-mali-red flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5" strokeWidth={2.4} />
      </span>
      <span className="min-w-0">
        <span className="block font-bold text-mali-navy dark:text-white text-[15px] leading-tight">{label}</span>
        {hint && <span className="block text-[11px] text-muted-foreground mt-0.5">{hint}</span>}
      </span>
    </button>
    {onSecondary && (
      <button
        type="button"
        onClick={onSecondary}
        data-testid={`${testid}-gallery`}
        className="shrink-0 w-10 h-10 rounded-xl bg-muted text-foreground flex items-center justify-center hover:bg-muted/70"
        aria-label="Choisir depuis la galerie"
        title="Choisir depuis la galerie"
      >
        <FolderOpen className="w-4 h-4" strokeWidth={2.4} />
      </button>
    )}
  </div>
);

const Thumb = ({ att, onRemove }) => {
  const url = mediaUrl(att.url);
  return (
    <div
      className="relative w-20 h-20 rounded-xl overflow-hidden border border-border bg-black/5 shrink-0 animate-in fade-in duration-200"
      data-testid={`evidence-thumb-${att.kind}`}
    >
      {att.kind === "image" && <img src={url} alt="" className="w-full h-full object-cover" />}
      {att.kind === "video" && (
        <div className="w-full h-full bg-black flex items-center justify-center text-white">
          <Play className="w-6 h-6" />
        </div>
      )}
      {att.kind === "audio" && (
        <div className="w-full h-full flex flex-col items-center justify-center bg-violet-500 text-white p-1">
          <Mic className="w-5 h-5" strokeWidth={2.5} />
          {att.duration_s != null && (
            <span className="text-[10px] font-bold mt-1 tabular-nums">{Math.round(att.duration_s)}s</span>
          )}
        </div>
      )}
      <button
        type="button"
        onClick={onRemove}
        data-testid={`evidence-remove-${att.kind}`}
        aria-label="Retirer"
        className="absolute top-1 right-1 w-6 h-6 rounded-full bg-rescue-red text-white flex items-center justify-center shadow-sm"
      >
        <X className="w-3 h-3" strokeWidth={3} />
      </button>
    </div>
  );
};

/**
 * Fullscreen redesigned evidence screen. Replaces the legacy bottom-sheet
 * picker. Mobile-first, three big action cards, instant thumbnails,
 * smart limits, optional description.
 */
export const HomeEvidenceDrawer = ({
  open, onOpenChange, attachments, onAttachmentsChange,
  description, onDescriptionChange, onBack, onContinue, t,
}) => {
  const camImgRef = useRef(null);
  const galImgRef = useRef(null);
  const camVidRef = useRef(null);
  const galVidRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [recorderOpen, setRecorderOpen] = useState(false);

  if (!open) return null;

  const nPhotos = countByKind(attachments, "image");
  const nVideos = countByKind(attachments, "video");
  const nAudio  = countByKind(attachments, "audio");
  const total = attachments.length;
  const countLabel = total === 0
    ? "0 preuve ajoutée"
    : total === 1 ? "1 preuve ajoutée" : `${total} preuves ajoutées`;

  const handleFile = async (file, kind) => {
    if (!file) return;
    try {
      if (kind === "image" && nPhotos >= MAX_PHOTOS) {
        toast.error(`Maximum ${MAX_PHOTOS} photos`);
        return;
      }
      if (kind === "video" && nVideos >= MAX_VIDEOS) {
        toast.error("Maximum 1 vidéo");
        return;
      }
      if (kind === "audio" && nAudio >= MAX_AUDIO) {
        toast.error("Maximum 1 audio");
        return;
      }

      let processed = file;
      let duration = null;
      if (kind === "video") {
        duration = await readMediaDuration(file, "video");
        if (duration > VIDEO_MAX_SECONDS + 0.5) {
          toast.error(`Vidéo trop longue, maximum ${VIDEO_MAX_SECONDS} secondes`);
          return;
        }
      } else if (kind === "audio") {
        duration = await readMediaDuration(file, "audio");
        if (duration > AUDIO_MAX_SECONDS + 0.5) {
          toast.error(`Audio trop long, maximum ${AUDIO_MAX_SECONDS} secondes`);
          return;
        }
      } else if (kind === "image") {
        processed = await resizePhoto(file);
      }

      setBusy(true);
      const att = await uploadMedia(processed);
      if (duration != null) att.duration_s = duration;
      onAttachmentsChange([...attachments, att]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'envoi du média");
    } finally {
      setBusy(false);
    }
  };

  const remove = (idx) =>
    onAttachmentsChange(attachments.filter((_, i) => i !== idx));

  return (
    <div
      className="fixed inset-0 z-[60] bg-background overflow-y-auto"
      data-testid="evidence-screen"
      role="dialog"
      aria-modal="true"
    >
      {/* Hidden file inputs */}
      <input ref={camImgRef} type="file" accept="image/*" capture="environment" hidden
             onChange={(e) => { handleFile(e.target.files?.[0], "image"); e.target.value = ""; }} />
      <input ref={galImgRef} type="file" accept="image/*" hidden
             onChange={(e) => { handleFile(e.target.files?.[0], "image"); e.target.value = ""; }} />
      <input ref={camVidRef} type="file" accept="video/*" capture="environment" hidden
             onChange={(e) => { handleFile(e.target.files?.[0], "video"); e.target.value = ""; }} />
      <input ref={galVidRef} type="file" accept="video/*" hidden
             onChange={(e) => { handleFile(e.target.files?.[0], "video"); e.target.value = ""; }} />

      <header className="sticky top-0 bg-background/95 backdrop-blur-xl px-4 pb-4 border-b border-border/60 pt-[calc(0.75rem+env(safe-area-inset-top))]">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            data-testid="evidence-back-btn"
            aria-label="Retour"
            className="w-9 h-9 rounded-lg hover:bg-muted flex items-center justify-center"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="min-w-0">
            <h2 className="font-display text-lg font-black tracking-tight text-mali-navy dark:text-white leading-tight">
              Ajouter des preuves
            </h2>
            <p className="text-[11px] text-muted-foreground leading-tight mt-0.5">
              Vous pouvez ajouter des preuves (optionnel)
            </p>
          </div>
        </div>
        <div
          className="mt-3 inline-flex items-center gap-2 bg-mali-yellow/15 text-amber-900 dark:text-mali-yellow border border-mali-yellow/40 rounded-full px-3 py-1"
          data-testid="evidence-count-pill"
        >
          <span className="text-[11px] font-bold tracking-wide tabular-nums">{countLabel}</span>
        </div>
      </header>

      <main className="px-4 pt-4 pb-32 space-y-3 max-w-md mx-auto">
        <ActionCard
          icon={Camera}
          label="Prendre une photo"
          hint={`Jusqu'à ${MAX_PHOTOS} photos · réduites automatiquement`}
          onPrimary={() => camImgRef.current?.click()}
          onSecondary={() => galImgRef.current?.click()}
          testid="evidence-card-photo"
        />
        <ActionCard
          icon={VideoIcon}
          label="Enregistrer une vidéo"
          hint={`1 vidéo · maximum ${VIDEO_MAX_SECONDS} secondes`}
          onPrimary={() => camVidRef.current?.click()}
          onSecondary={() => galVidRef.current?.click()}
          testid="evidence-card-video"
        />
        <ActionCard
          icon={Mic}
          label="Enregistrer un audio"
          hint={`1 audio · maximum ${AUDIO_MAX_SECONDS} secondes`}
          onPrimary={() => {
            if (nAudio >= MAX_AUDIO) {
              toast.error("Maximum 1 audio");
              return;
            }
            setRecorderOpen(true);
          }}
          testid="evidence-card-audio"
        />

        {recorderOpen && (
          <AudioRecorder
            onCancel={() => setRecorderOpen(false)}
            onSaved={(att) => {
              onAttachmentsChange([...attachments, att]);
              setRecorderOpen(false);
            }}
          />
        )}

        {busy && (
          <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground py-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Envoi en cours…</span>
          </div>
        )}

        {attachments.length > 0 && (
          <div
            className="flex flex-wrap gap-2 pt-1"
            data-testid="evidence-thumb-list"
          >
            {attachments.map((a, i) => (
              <Thumb key={`${a.url}-${i}`} att={a} onRemove={() => remove(i)} />
            ))}
          </div>
        )}

        <div className="pt-2">
          <textarea
            data-testid="description-input"
            value={description}
            onChange={(e) => onDescriptionChange(e.target.value)}
            rows={3}
            placeholder="Décrivez brièvement la situation (optionnel)"
            className="w-full bg-card border border-border rounded-xl p-3 text-sm placeholder:text-muted-foreground focus:ring-2 focus:ring-mali-navy/30 focus:border-mali-navy/40 transition-all resize-none"
          />
        </div>
      </main>

      <footer
        className="fixed bottom-0 left-0 right-0 z-[70] bg-background/95 backdrop-blur-xl px-4 py-3 border-t border-border/60"
        data-testid="evidence-footer"
        style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
      >
        <div className="flex gap-2 max-w-md mx-auto w-full">
          <button
            type="button"
            onClick={onContinue}
            data-testid="evidence-no-evidence-btn"
            className="flex-1 bg-muted text-foreground font-bold text-sm py-3.5 rounded-xl hover:bg-muted/70 transition-colors"
          >
            Aucune preuve
          </button>
          <button
            type="button"
            onClick={onContinue}
            data-testid="evidence-continue-btn"
            className="flex-[2] bg-mali-red text-white font-bold text-sm py-3.5 rounded-xl shadow-md shadow-mali-red/30 hover:opacity-95 active:scale-[0.99] transition-all"
          >
            {attachments.length > 0 ? "Continuer avec les preuves" : "Continuer"}
          </button>
        </div>
      </footer>
    </div>
  );
};
